import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Analytics } from "../../api";
import { fmtMoney } from "../../lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { EmptyState } from "../ui/empty-state";

/** Xulosa uchun eng kam ish soni — bundan kami tasodif, qonuniyat emas. */
const MIN_WORKS_FOR_SUMMARY = 20;
/** Oyna shu ulushdan kam bo'lsa "eng band vaqt" degan gap ma'nosiz. */
const MIN_WINDOW_SHARE = 10;
const WINDOW_HOURS = 3;

export type BusiestWindow = { weekday: number; from: number; sharePct: number };

/**
 * Eng band 3 soatlik oyna — har hafta kuni bo'yicha siljuvchi yig'indi.
 *
 * Yangi ma'lumot O'YLAB TOPILMAYDI: bu shu yerda chizilgan 7×24 to'rning
 * o'zidan olingan maksimum, ya'ni foydalanuvchi uni to'rga qarab tekshira
 * oladi.
 */
export function busiestWindow(cells: Analytics["heatmap"]): BusiestWindow | null {
  const total = cells.reduce((s, c) => s + c.count, 0);
  if (total < MIN_WORKS_FOR_SUMMARY) return null;

  const grid: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0));
  for (const c of cells) {
    if (c.weekday >= 0 && c.weekday < 7 && c.hour >= 0 && c.hour < 24) {
      grid[c.weekday][c.hour] += c.count;
    }
  }

  let best: BusiestWindow | null = null;
  for (let wd = 0; wd < 7; wd++) {
    for (let h = 0; h <= 24 - WINDOW_HOURS; h++) {
      let sum = 0;
      for (let k = 0; k < WINDOW_HOURS; k++) sum += grid[wd][h + k];
      const sharePct = (sum / total) * 100;
      if (!best || sharePct > best.sharePct) best = { weekday: wd, from: h, sharePct };
    }
  }
  if (!best || best.sharePct < MIN_WINDOW_SHARE) return null;
  return best;
}

export function HeatmapCard({ cells }: { cells: Analytics["heatmap"] }) {
  const { t } = useTranslation("analytics");
  const busiest = useMemo(() => busiestWindow(cells), [cells]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">{t("busyHours.title")}</CardTitle>
          <p className="text-xs text-muted-foreground">{t("busyHours.subtitle")}</p>
        </div>
        {busiest && (
          <p className="text-sm">
            <span className="text-muted-foreground">{t("busyHours.busiestLabel")}</span>{" "}
            <strong className="tabular-nums">
              {t("busyHours.busiestValue", {
                day: t(`weekdaysFull.${busiest.weekday}`),
                from: busiest.from,
                to: busiest.from + 3,
              })}
            </strong>{" "}
            <span className="text-muted-foreground tabular-nums">
              {t("busyHours.busiestShare", { pct: busiest.sharePct.toFixed(0) })}
            </span>
          </p>
        )}
      </CardHeader>
      <CardContent>
        <Heatmap cells={cells} t={t} />
      </CardContent>
    </Card>
  );
}

function Heatmap({ cells, t }: { cells: Analytics["heatmap"]; t: TFunction }) {
  const weekdays = [0, 1, 2, 3, 4, 5, 6].map((i) => t(`weekdays.${i}`));
  const maxCount = cells.reduce((m, c) => Math.max(m, c.count), 0);
  const grid = new Map<string, { count: number; revenue: string }>();
  for (const c of cells) {
    grid.set(`${c.weekday}-${c.hour}`, { count: c.count, revenue: c.revenue });
  }
  if (maxCount === 0) {
    return <EmptyState title={t("empty.noWorks")} />;
  }
  return (
    <div className="overflow-x-auto">
      <div
        className="grid gap-0.5 min-w-[560px]"
        style={{ gridTemplateColumns: "36px repeat(24, minmax(18px, 1fr))" }}
      >
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div
            key={`h-${h}`}
            className="text-[10px] text-muted-foreground flex items-center justify-center font-medium"
          >
            {h % 3 === 0 ? h : ""}
          </div>
        ))}
        {weekdays.map((wd, wIdx) => (
          <HmRow key={wIdx} weekday={wIdx} label={wd} grid={grid} max={maxCount} t={t} />
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 mt-2 text-[11px] text-muted-foreground">
        <span>{t("heatmap.less")}</span>
        <span className="inline-flex h-2.5 w-24 rounded-sm bg-gradient-to-r from-muted to-primary" />
        <span>{t("heatmap.more")}</span>
      </div>
    </div>
  );
}

function HmRow({
  weekday,
  label,
  grid,
  max,
  t,
}: {
  weekday: number;
  label: string;
  grid: Map<string, { count: number; revenue: string }>;
  max: number;
  t: TFunction;
}) {
  return (
    <>
      <div className="text-[10px] text-muted-foreground flex items-center justify-center font-medium">
        {label}
      </div>
      {Array.from({ length: 24 }, (_, h) => {
        const cell = grid.get(`${weekday}-${h}`);
        const intensity = cell ? cell.count / max : 0;
        const empty = !cell;
        return (
          <div
            key={h}
            className={
              "h-5 rounded-sm transition-transform hover:scale-110 " +
              (empty ? "bg-muted" : "bg-primary")
            }
            style={empty ? undefined : { opacity: Math.max(0.15, intensity) }}
            title={
              cell
                ? t("heatmap.cellTitle", {
                    label,
                    hour: h,
                    count: cell.count,
                    revenue: fmtMoney(cell.revenue),
                  })
                : t("heatmap.cellEmptyTitle", { label, hour: h })
            }
          />
        );
      })}
    </>
  );
}
